import { PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from 'react'
import { OcrRegion } from '@api/ocr/api'
import { pointerPoint, rectangleBetween } from './region'

interface Options {
    disabled: boolean
    resetKey: string
    onDrawStart?: () => void
    onDrawChange: (region: OcrRegion) => void
    onDrawEnd: (region: OcrRegion) => void
    onDrawCancel: () => void
    onTap?: (target: EventTarget | null) => void
}
interface Gesture {
    mode: 'draw' | 'pan'
    pointerId: number
    target: HTMLDivElement
    startX: number
    startY: number
    scrollLeft: number
    scrollTop: number
    start: {x: number; y: number}
    region: OcrRegion
    origin: EventTarget | null
}

/** A gesture keeps its initial mode. Modifier changes can cancel drawing, never turn a pan into a draw. */
export function useDocumentGestures(options: Options) {
    const latest = useRef(options); latest.current = options
    const viewportRef = useRef<HTMLDivElement>(null)
    const active = useRef<Gesture | null>(null)
    const suppressClick = useRef(false)
    const [mode, setMode] = useState<Gesture['mode'] | null>(null)
    const [ctrlPressed, setCtrlPressed] = useState(false)

    const finish = (commit: boolean) => {
        const gesture = active.current
        if (!gesture) return
        active.current = null; setMode(null)
        try {gesture.target.releasePointerCapture?.(gesture.pointerId)} catch { /* Capture may already be lost. */ }
        if (gesture.mode === 'draw') {
            if (commit) latest.current.onDrawEnd(gesture.region)
            else latest.current.onDrawCancel()
        }
    }
    useEffect(() => {
        const keyDown = (event: KeyboardEvent) => {if (event.key === 'Control') setCtrlPressed(true)}
        const keyUp = (event: KeyboardEvent) => {if (event.key === 'Control') {setCtrlPressed(event.ctrlKey); if (!event.ctrlKey && active.current?.mode === 'draw') finish(false)}}
        const blur = () => {setCtrlPressed(false); finish(false)}
        window.addEventListener('keydown', keyDown); window.addEventListener('keyup', keyUp); window.addEventListener('blur', blur)
        return () => {window.removeEventListener('keydown', keyDown); window.removeEventListener('keyup', keyUp); window.removeEventListener('blur', blur); finish(false)}
        // The callbacks read current options through a ref.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    useEffect(() => {finish(false)}, [options.resetKey, options.disabled]) // eslint-disable-line react-hooks/exhaustive-deps

    const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
        if (event.button !== 0 || latest.current.disabled || active.current) return
        const viewport = viewportRef.current
        if (!viewport) return
        event.preventDefault(); setCtrlPressed(event.ctrlKey)
        const start = pointerPoint(event.clientX, event.clientY, event.currentTarget.getBoundingClientRect())
        const kind = event.ctrlKey ? 'draw' : 'pan'
        const region = rectangleBetween(start, start)
        suppressClick.current = kind === 'draw'
        active.current = {mode: kind, pointerId: event.pointerId, target: event.currentTarget, origin: event.target, startX: event.clientX, startY: event.clientY, scrollLeft: viewport.scrollLeft, scrollTop: viewport.scrollTop, start, region}
        setMode(kind)
        if (kind === 'draw') {latest.current.onDrawStart?.(); latest.current.onDrawChange(region)}
        event.currentTarget.setPointerCapture?.(event.pointerId)
    }
    const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
        setCtrlPressed(event.ctrlKey)
        const gesture = active.current
        if (!gesture || gesture.pointerId !== event.pointerId) return
        if (gesture.mode === 'pan') {
            const dx = event.clientX - gesture.startX, dy = event.clientY - gesture.startY
            if (Math.hypot(dx, dy) > 4) suppressClick.current = true
            if (viewportRef.current) {viewportRef.current.scrollLeft = Math.max(0, gesture.scrollLeft - dx); viewportRef.current.scrollTop = Math.max(0, gesture.scrollTop - dy)}
        } else {
            if (!event.ctrlKey) {setCtrlPressed(false); finish(false); return}
            gesture.region = rectangleBetween(gesture.start, pointerPoint(event.clientX, event.clientY, event.currentTarget.getBoundingClientRect()))
            latest.current.onDrawChange(gesture.region)
        }
    }
    const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
        const gesture = active.current
        if (!gesture || gesture.pointerId !== event.pointerId) return
        onPointerMove(event)
        if (!active.current) return
        setCtrlPressed(event.ctrlKey)
        finish(gesture.mode !== 'draw' || event.ctrlKey)
        if (gesture.mode === 'pan' && !suppressClick.current) latest.current.onTap?.(gesture.origin)
    }
    const cancelPointer = (event: ReactPointerEvent<HTMLDivElement>) => {if (active.current?.pointerId === event.pointerId) finish(false)}
    return {
        viewportRef,
        handlers: {onPointerDown, onPointerMove, onPointerUp, onPointerCancel: cancelPointer, onLostPointerCapture: cancelPointer},
        cursor: mode === 'pan' ? 'grabbing' : ctrlPressed ? 'crosshair' : 'grab',
        gestureActive: mode !== null,
        isGestureActive: () => active.current !== null,
        shouldSuppressClick: () => suppressClick.current
    }
}
